import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Sparkles,
  Globe2,
  MapPin,
  Pencil,
  X,
  Plus,
  Trash2,
  ChevronUp,
  ChevronDown,
  ArrowRight,
  Save,
  RotateCcw,
} from "lucide-react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, apiErrorMessage } from "@/lib/api";

/**
 * Modal ampliado para VER + EDITAR una guía editorial.
 *
 * Props:
 *   - guide: plantilla editorial completa (con `suggestions[]`) o null
 *   - onClose: () => void
 *   - onConvertToTd: (guide) => void
 *   - categoryMeta: dict CATEGORY_META → {label, color}
 */
export default function GuideEditorDialog({ guide, onClose, onConvertToTd, categoryMeta = {} }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    if (!guide) {
      setEditing(false);
      setDraft(null);
      return;
    }
    setDraft({
      name: guide.name || "",
      description: guide.description || "",
      suggestions: (guide.suggestions || []).map((s) => ({
        name: s.name || "",
        address: s.address || "",
        website: s.website || "",
        image: s.image || "",
        description: s.description || "",
      })),
    });
  }, [guide]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = {
        name: draft.name,
        description: draft.description,
        suggestions: draft.suggestions,
      };
      return (await api.put(`/plantillas/${guide.id}`, payload)).data;
    },
    onSuccess: () => {
      toast.success("Guía actualizada");
      qc.invalidateQueries({ queryKey: ["editorial-list"] });
      qc.invalidateQueries({ queryKey: ["editorial-summary"] });
      setEditing(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (!guide || !draft) return null;

  const suggestions = draft.suggestions;
  const catMeta = categoryMeta[guide.editorial_category];

  const updateSug = (i, patch) => {
    setDraft((d) => ({
      ...d,
      suggestions: d.suggestions.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    }));
  };
  const addSug = () => {
    setDraft((d) => ({
      ...d,
      suggestions: [
        ...d.suggestions,
        { name: "", address: "", website: "", image: "", description: "" },
      ],
    }));
  };
  const removeSug = (i) => {
    setDraft((d) => ({ ...d, suggestions: d.suggestions.filter((_, j) => j !== i) }));
  };
  const moveSug = (i, dir) => {
    setDraft((d) => {
      const arr = [...d.suggestions];
      const j = i + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return { ...d, suggestions: arr };
    });
  };

  const resetDraft = () => {
    setDraft({
      name: guide.name || "",
      description: guide.description || "",
      suggestions: (guide.suggestions || []).map((s) => ({
        name: s.name || "",
        address: s.address || "",
        website: s.website || "",
        image: s.image || "",
        description: s.description || "",
      })),
    });
  };

  return (
    <Dialog open={!!guide} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="flex h-[92vh] max-w-5xl flex-col overflow-hidden p-0"
        data-testid="guide-editor-dialog"
      >
        {/* Header */}
        <DialogHeader className="shrink-0 border-b bg-white p-4">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {editing ? (
                <Input
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Nombre de la guía"
                  className="text-lg font-semibold"
                  data-testid="guide-name-input"
                />
              ) : (
                <DialogTitle className="pr-6 text-lg" data-testid="guide-title">
                  {draft.name}
                </DialogTitle>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="outline">{guide.type}</Badge>
                {guide.editorial_category && (
                  <Badge className={catMeta?.color}>{catMeta?.label || guide.editorial_category}</Badge>
                )}
                {guide.location && (
                  <span className="flex items-center gap-1 text-slate-500">
                    <MapPin className="h-3 w-3" />
                    {guide.location}
                  </span>
                )}
                {suggestions.length > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                    <Sparkles className="h-3 w-3" />
                    {suggestions.length} sugerencia{suggestions.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!editing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditing(true)}
                  data-testid="guide-edit-button"
                >
                  <Pencil className="mr-1.5 h-4 w-4" /> Editar
                </Button>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      resetDraft();
                      setEditing(false);
                    }}
                    data-testid="guide-cancel-button"
                  >
                    Cancelar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetDraft}
                    data-testid="guide-reset-button"
                    title="Descartar cambios y volver al original"
                  >
                    <RotateCcw className="mr-1.5 h-4 w-4" /> Resetear
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveMut.mutate()}
                    disabled={saveMut.isPending}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="guide-save-button"
                  >
                    <Save className="mr-1.5 h-4 w-4" />
                    {saveMut.isPending ? "Guardando…" : "Guardar"}
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon" onClick={onClose} data-testid="guide-close-button">
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4" data-testid="guide-scroll">
          {/* Description */}
          <section className="mb-6 rounded-lg border bg-white p-4">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Descripción / Introducción
              </h3>
              {editing && (
                <span className="text-[10px] text-slate-400">HTML permitido</span>
              )}
            </div>
            {editing ? (
              <Textarea
                value={draft.description}
                onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                rows={8}
                className="font-mono text-xs"
                placeholder="<p>Introducción de la guía…</p>"
                data-testid="guide-description-input"
              />
            ) : draft.description ? (
              <div
                className="prose prose-sm max-w-none text-slate-800"
                dangerouslySetInnerHTML={{ __html: draft.description }}
              />
            ) : (
              <p className="text-sm italic text-slate-400">Sin descripción</p>
            )}
          </section>

          {/* Suggestions */}
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
                Sugerencias / Recomendaciones ({suggestions.length})
              </h3>
              {editing && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addSug}
                  data-testid="guide-add-suggestion"
                >
                  <Plus className="mr-1.5 h-4 w-4" /> Añadir POI
                </Button>
              )}
            </div>

            {suggestions.length === 0 ? (
              <div className="rounded-lg border border-dashed p-6 text-center text-sm text-slate-400">
                Sin sugerencias. {editing && "Pulsa \u201cAñadir POI\u201d arriba para agregar la primera."}
              </div>
            ) : (
              <div className="space-y-3">
                {suggestions.map((s, i) =>
                  editing ? (
                    <SuggestionEditRow
                      key={`sug-${i}`}
                      idx={i}
                      total={suggestions.length}
                      sug={s}
                      onChange={(patch) => updateSug(i, patch)}
                      onRemove={() => removeSug(i)}
                      onMove={(dir) => moveSug(i, dir)}
                    />
                  ) : (
                    <SuggestionViewRow key={`sug-${i}`} idx={i} sug={s} />
                  )
                )}
              </div>
            )}
          </section>
        </div>

        {/* Footer */}
        <DialogFooter className="shrink-0 justify-between border-t bg-white p-3">
          <Button
            variant="outline"
            onClick={() => onConvertToTd?.(guide)}
            disabled={editing}
            data-testid="editorial-convert-to-td"
          >
            <ArrowRight className="mr-1.5 h-4 w-4" /> Convertir a Travel Document
          </Button>
          <div className="text-[11px] text-slate-400">
            ID: {guide.id?.slice(0, 8)}… {guide.travefy_suggestions_synced_at ? "· sync Travefy " + guide.travefy_suggestions_synced_at.slice(0, 10) : ""}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Sub-componentes ──────────────────────────────────────────────────────

function SuggestionViewRow({ idx, sug }) {
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-3 sm:flex-row"
      data-testid={`suggestion-view-${idx}`}
    >
      {sug.image ? (
        <div className="w-full flex-shrink-0 overflow-hidden rounded sm:w-40">
          <img
            src={sug.image}
            alt={sug.name}
            loading="lazy"
            className="h-24 w-full object-cover"
            onError={(e) => {
              e.currentTarget.parentElement.style.display = "none";
            }}
          />
        </div>
      ) : (
        <div className="flex h-24 w-full flex-shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] uppercase text-slate-400 sm:w-40">
          Sin imagen
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{sug.name || <em>Sin nombre</em>}</p>
        {sug.address && (
          <p className="mt-0.5 text-[11px] uppercase tracking-wider text-slate-500">{sug.address}</p>
        )}
        {sug.description && (
          <div
            className="mt-1.5 text-xs text-slate-700 [&_a]:underline [&_p]:my-1"
            dangerouslySetInnerHTML={{ __html: sug.description }}
          />
        )}
        {sug.website && (
          <a
            href={sug.website}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700 hover:underline"
          >
            <Globe2 className="h-3 w-3" /> Website
          </a>
        )}
      </div>
    </div>
  );
}

function SuggestionEditRow({ idx, total, sug, onChange, onRemove, onMove }) {
  return (
    <div
      className="rounded-lg border border-emerald-200 bg-white p-3"
      data-testid={`suggestion-edit-${idx}`}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700">
          POI #{idx + 1}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onMove(-1)}
            disabled={idx === 0}
            className="h-7 w-7"
            data-testid={`suggestion-up-${idx}`}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onMove(1)}
            disabled={idx === total - 1}
            className="h-7 w-7"
            data-testid={`suggestion-down-${idx}`}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-7 w-7 text-red-600 hover:bg-red-50 hover:text-red-700"
            data-testid={`suggestion-remove-${idx}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="space-y-1 md:col-span-1">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500">Imagen (URL)</Label>
          <Input
            value={sug.image}
            onChange={(e) => onChange({ image: e.target.value })}
            placeholder="https://…jpg"
            className="text-xs"
            data-testid={`suggestion-image-${idx}`}
          />
          {sug.image && (
            <img
              src={sug.image}
              alt=""
              className="mt-1 h-20 w-full rounded object-cover"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          )}
        </div>
        <div className="space-y-3 md:col-span-2">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Nombre</Label>
            <Input
              value={sug.name}
              onChange={(e) => onChange({ name: e.target.value })}
              placeholder="Restaurante La Marina"
              data-testid={`suggestion-name-${idx}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Dirección</Label>
            <Input
              value={sug.address}
              onChange={(e) => onChange({ address: e.target.value })}
              placeholder="Calle Mayor, 12 · Ciudad · País"
              data-testid={`suggestion-address-${idx}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">Website</Label>
            <Input
              value={sug.website}
              onChange={(e) => onChange({ website: e.target.value })}
              placeholder="https://…"
              data-testid={`suggestion-website-${idx}`}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500">
              Descripción (HTML permitido)
            </Label>
            <Textarea
              value={sug.description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={4}
              className="font-mono text-xs"
              placeholder="<p>Descripción breve del sitio…</p>"
              data-testid={`suggestion-description-${idx}`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
